/**
 * Rubriques de l'application : une seule liste nourrit la colonne latérale,
 * la barre du bas, le Menu et le raccourci personnel (décision du 17/10/2026).
 * Une rubrique visible à un endroit l'est partout, avec le même filtrage par
 * droits que les routes (`app/routes.tsx`) : on ne propose jamais un écran qui
 * répondrait 403.
 *
 * La vue décide de la liste : la vue terrain (technicien, ou responsable en
 * vue Terrain) garde sa journée, ses tâches et ses outils ; le bureau a ses
 * quatre groupes. Les adresses restent ouvertes selon les droits dans les deux
 * vues, seule la navigation proposée change.
 */
import type { LucideIcon } from 'lucide-react';
import type { Capability, SpecializedModule } from '@myequip/shared';
import { t } from '../core/i18n.js';
import { ICONS } from '../design/icons.js';
import { routeAllowed, type RouteAccess } from './route-types.js';
import { SALES_PATHS, salesEntry, salesSections } from './sales-sections.js';
import { PLANNING_READ_RIGHTS } from '../features/interventions/list/tabs.js';
import { MATERIEL_ANY, MATERIEL_PATHS, materielSections } from './materiel-sections.js';

export type AreaGroup = 'principal' | 'clients' | 'operations' | 'pilotage';

export interface Area {
  /** Adresse d'entrée de la rubrique. */
  to: string;
  label: string;
  icon: LucideIcon;
  group: AreaGroup;
  /** Une seule de ces capacités suffit pour proposer la rubrique. */
  anyOf?: readonly Capability[];
  module?: SpecializedModule;
  /** Autres adresses de la rubrique (fiches, pages liées) : état actif. */
  also?: readonly string[];
  /** Active seulement sur son adresse exacte (l'accueil). */
  end?: boolean;
  /** Onglets de la rubrique à leur adresse : la recherche les trouve aussi. */
  tabs?: readonly { label: string; to: string }[];
  /** Ce qui attend dans la rubrique (messages non lus) : pastille sur son icône (`NavBadge`). */
  count?: number | undefined;
  /** Ce que compte la pastille, lu par les lecteurs d'écran (« non lus »). */
  countLabel?: string;
}

export interface AreaSection {
  group: AreaGroup;
  title?: string;
  areas: Area[];
}

const HOME_PATH = '/';
const SALES_ALSO = [SALES_PATHS.devis, SALES_PATHS.factures, SALES_PATHS.contrats, '/propositions-maintenance'];
const PLANNING_PATH = '/planning';

const INTERVENTIONS_PATH = '/interventions';

/** Sans droit de lire le planning (une comptabilité), la rubrique n'est ni proposée ni vide dans la barre du bas. */
const planning: Area = {
  to: PLANNING_PATH, label: t('planning.titre'), icon: ICONS.planning, group: 'principal', anyOf: PLANNING_READ_RIGHTS,
};
/**
 * Espace Interventions (17/10/2026) : les demandes et les interventions, par
 * étape. Au bureau, il porte les fiches d'intervention ; au terrain, c'est le
 * planning qui les porte, la journée s'y ouvrant.
 */
const interventions: Area = {
  to: INTERVENTIONS_PATH, label: t('nav.interventions'), icon: ICONS.intervention, group: 'principal',
  anyOf: ['intervention.create_any', 'planning.view_all'], also: ['/demandes'],
};
const fieldPlanning: Area = { ...planning, also: [INTERVENTIONS_PATH] };
const tasks: Area = { to: '/taches', label: t('nav.taches'), icon: ICONS.taches, group: 'principal' };
/**
 * Messages (25/10/2026) : annonces de l'entreprise et conversations de l'équipe,
 * au bureau comme au terrain. L'entreprise peut couper la rubrique : son droit
 * disparaît alors pour tous (`/auth/me`), et l'entrée avec.
 */
export const MESSAGES_AREA_PATH = '/messages';
const messages: Area = {
  to: MESSAGES_AREA_PATH, label: t('nav.messages'), icon: ICONS.messages, group: 'principal', anyOf: ['messaging.use'],
  countLabel: t('messages.nonLusPastille'),
};
const customers: Area = {
  to: '/clients', label: t('nav.clients'), icon: ICONS.clients, group: 'clients', anyOf: ['customer.view'],
  also: ['/equipements'],
};
const time: Area = {
  to: '/temps', label: t('temps.titre'), icon: ICONS.temps, group: 'operations', anyOf: ['time.record_own'],
};
const fluids: Area = {
  to: '/fluides', label: t('fluides.titre'), icon: ICONS.fluides, group: 'operations', anyOf: ['fluids.record'],
  module: 'refrigerants',
};
const analytics: Area = {
  to: '/analyses', label: t('nav.analyses'), icon: ICONS.analyses, group: 'pilotage', anyOf: ['analytics.view'],
};

/** Bureau : accueil et planning, clients et ventes, opérations, pilotage. */
const OFFICE_AREAS: readonly Area[] = [
  { to: HOME_PATH, label: t('nav.accueil'), icon: ICONS.accueil, group: 'principal', end: true },
  planning,
  interventions,
  tasks,
  messages,
  customers,
  // Une seule rubrique Ventes : Devis · Factures · Contrats en onglets (plan du 17/10/2026).
  {
    to: SALES_PATHS.devis, label: t('nav.ventes'), icon: ICONS.ventes, group: 'clients',
    anyOf: ['quote.manage', 'invoice.view', 'contract.view'],
    also: [SALES_PATHS.factures, SALES_PATHS.contrats, '/propositions-maintenance'],
  },
  {
    to: '/avis-clients', label: t('nav.avisClients'), icon: ICONS.avis, group: 'clients', anyOf: ['feedback.view'],
  },
  // Un seul espace Équipe (18/10/2026) : Disponibilités · Décisions, chacune à son adresse.
  {
    to: '/equipe', label: t('nav.equipe'), icon: ICONS.equipe, group: 'operations',
    anyOf: ['planning.view_all', 'absence.approve'], also: ['/personnel', '/decisions', '/validations'],
  },
  time,
  // Une seule rubrique Matériel : le stock et les achats, factures reçues comprises (17/10/2026).
  {
    to: MATERIEL_PATHS.accueil, label: t('nav.materiel'), icon: ICONS.stock, group: 'operations',
    anyOf: MATERIEL_ANY, also: ['/stock', '/fournisseurs'],
  },
  fluids,
  analytics,
  {
    to: '/entreprise', label: t('nav.monEntreprise'), icon: ICONS.entreprise, group: 'pilotage',
    anyOf: ['settings.manage', 'users.manage', 'audit.view_full'],
  },
  {
    to: '/conflits', label: t('nav.conflits'), icon: ICONS.conflits, group: 'pilotage', anyOf: ['conflict.arbitrate'],
  },
];

/** Terrain : sa journée, ses tâches, puis les outils du travail sur place. */
const FIELD_AREAS: readonly Area[] = [
  { to: HOME_PATH, label: t('nav.maJournee'), icon: ICONS.accueil, group: 'principal', end: true },
  fieldPlanning,
  tasks,
  messages,
  { ...customers, group: 'operations' },
  time,
  { to: MATERIEL_PATHS.stock, label: t('nav.monVehicule'), icon: ICONS.vehicule, group: 'operations', also: ['/stock'] },
  // Tickets payés sur le terrain (repas, carburant, péage) : saisis avec leur photo.
  {
    to: '/depenses', label: t('nav.depenses'), icon: ICONS.depenses, group: 'operations', anyOf: ['expense.record'],
    also: ['/fournisseurs/factures'],
  },
  fluids,
  // Absente par défaut : n'apparaît que si le patron a ouvert les analyses à ce membre.
  analytics,
];

/** Rubrique proposée par défaut dans l'emplacement personnel de la barre du bas. */
export const SHORTCUT_DEFAULT = { terrain: '/taches', bureau: INTERVENTIONS_PATH } as const;

/**
 * Raccourcis enregistrés sous une ancienne adresse de rubrique : « Demandes »
 * est devenue l'espace Interventions le 17/10/2026, le choix est gardé.
 */
const LEGACY_SHORTCUTS: Readonly<Record<string, string>> = {
  '/demandes': INTERVENTIONS_PATH,
  // Mon entreprise : `/parametres` est devenu `/entreprise` (refonte v4).
  '/parametres': '/entreprise',
};

export interface AreaContext {
  terrain: boolean;
  access: RouteAccess;
  /** Faux seulement quand on SAIT qu'aucun conflit n'attend : l'entrée se retire. */
  conflictsPending: boolean;
  /** Non-lus de la rubrique Messages (conversations et annonces), quand ils sont lus. */
  unreadMessages?: number | undefined;
}

/** Les listes du Matériel que ce compte peut ouvrir : la recherche « Aller à… » les trouve. */
function materielTabs(access: RouteAccess): { label: string; to: string }[] {
  return materielSections(access.can).map((section) => ({ label: t(`stock.materiel.tuiles.${section}`), to: MATERIEL_PATHS[section] }));
}

/** Les rubriques que ce compte peut ouvrir, dans l'ordre de la navigation. */
export function areasFor(context: AreaContext): Area[] {
  const { access } = context;
  const base = context.terrain ? FIELD_AREAS : OFFICE_AREAS;
  return base
    .map((area) => {
      if (area.to === MATERIEL_PATHS.accueil) return { ...area, tabs: materielTabs(access) };
      if (area.to === MESSAGES_AREA_PATH) return { ...area, count: context.unreadMessages };
      if (area.to !== SALES_PATHS.devis) return area;
      // La rubrique Ventes s'ouvre sur le premier onglet que les droits permettent.
      const entry = salesEntry(access.can);
      const tabs = salesSections(access.can).map((section) => ({ label: t(`nav.ventesOnglets.${section.key}`), to: section.to }));
      return { ...area, to: entry, also: SALES_ALSO.filter((path) => path !== entry), tabs };
    })
    .filter((area) => routeAllowed(area, access))
    .filter((area) => area.to !== '/conflits' || context.conflictsPending);
}

const GROUP_TITLES: Record<AreaGroup, { bureau?: string; terrain?: string }> = {
  principal: {},
  clients: { bureau: t('nav.groupeClients') },
  operations: { bureau: t('nav.groupeOperations'), terrain: t('nav.groupeTerrain') },
  pilotage: { bureau: t('nav.groupePilotage'), terrain: t('nav.groupePilotage') },
};

/** Les rubriques regroupées comme dans la colonne et le Menu ; un groupe vide disparaît. */
export function areaSections(areas: readonly Area[], terrain: boolean): AreaSection[] {
  const order: AreaGroup[] = ['principal', 'clients', 'operations', 'pilotage'];
  return order
    .map((group) => {
      const title = GROUP_TITLES[group][terrain ? 'terrain' : 'bureau'];
      return { group, ...(title ? { title } : {}), areas: areas.filter((area) => area.group === group) };
    })
    .filter((section) => section.areas.length > 0);
}

/** Vrai sur l'adresse de la rubrique et sur celles qu'elle regroupe. */
export function areaMatches(area: Area, pathname: string): boolean {
  if (area.end) return pathname === area.to;
  return [area.to, ...(area.also ?? [])].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export interface BottomBar {
  home: Area | undefined;
  /** Deuxième bouton : le planning, ou à défaut les ventes puis les clients (comptabilité). */
  second: Area | undefined;
  /** Quatrième bouton, choisi par la personne parmi `options`. */
  shortcut: Area | undefined;
  options: Area[];
  /** Une rubrique hors de la barre attend (pastille) : un point sur « Menu », qui la contient. */
  menuWaiting: boolean;
}

const SALES_ENTRIES: readonly string[] = Object.values(SALES_PATHS);

/**
 * Barre du bas, logique pure : Accueil, Planning, Créer, un raccourci, Menu.
 * Qui ne lit pas le planning (une comptabilité) a Ventes à la place, ou à
 * défaut Clients : jamais un onglet vide. Le raccourci est le premier choix
 * enregistré encore permis (les anciens comptes en ont enregistré deux : le
 * premier est gardé), sinon le défaut de la vue, sinon la première rubrique
 * permise. Il n'accorde aucun droit.
 */
export function bottomBar(areas: readonly Area[], chosen: readonly string[] | null | undefined, fallback: string): BottomBar {
  const home = areas.find((area) => area.to === HOME_PATH);
  const second = areas.find((area) => area.to === PLANNING_PATH)
    ?? areas.find((area) => SALES_ENTRIES.includes(area.to))
    ?? areas.find((area) => area.to === '/clients');
  const options = areas.filter((area) => area !== home && area !== second);
  const pick = (path: string | undefined): Area | undefined =>
    path === undefined ? undefined : options.find((area) => area.to === (LEGACY_SHORTCUTS[path] ?? path));
  const shortcut = (chosen ?? []).map(pick).find(Boolean) ?? pick(fallback) ?? options[0];
  const menuWaiting = areas.some((area) => (area.count ?? 0) > 0 && area !== home && area !== second && area !== shortcut);
  return { home, second, shortcut, options, menuWaiting };
}
